/**
 * The permission vocabulary, mirroring libs/auth/src/permissions.ts in store-backend.
 *
 * CLIENT-SIDE CHECKS ARE PRESENTATION ONLY. They decide which navigation items and buttons are
 * worth rendering. The API refuses the same calls with 403 whatever this file says, which is what
 * actually protects the data — being wrong here shows the wrong buttons, never the wrong data.
 *
 * Kept as a union rather than imported from the backend because the two deploy independently: a
 * permission the API has and this build has not simply renders nothing, instead of failing to
 * compile against a package version that is not there yet.
 */
export const PERMISSIONS = [
  'dashboard:read',
  'catalogue:read',
  'catalogue:write',
  'order:read',
  'order:write',
  'shipment:read',
  'shipment:write',
  'inventory:read',
  'inventory:write',
  'customer:read',
  'report:read',
  'activity:read',
  'notification:read',
  'staff:manage',
  'settings:manage',
] as const

export type Permission = (typeof PERMISSIONS)[number]

/** Groups, in the order the role editor lays them out. Must match the API's catalogue. */
export const PERMISSION_GROUPS = [
  'Overview',
  'Catalogue',
  'Orders',
  'Fulfilment',
  'Customers',
  'Insights',
  'Administration',
] as const

export type PermissionGroup = (typeof PERMISSION_GROUPS)[number]

interface PermissionHolder {
  readonly isSuperAdmin?: boolean
  readonly permissions?: readonly string[]
}

/**
 * Whether the signed-in staff member holds a permission.
 *
 * Super admin short-circuits, matching the guard: that role holds every permission including ones
 * added after this bundle was built, so testing its list would hide screens from the one account
 * that must never lose them.
 */
export function can(user: PermissionHolder | null | undefined, permission: Permission): boolean {
  if (!user) return false
  if (user.isSuperAdmin === true) return true
  return (user.permissions ?? []).includes(permission)
}

/** True when the user holds at least one of these. For nav items that cover several screens. */
export function canAny(
  user: PermissionHolder | null | undefined,
  permissions: readonly Permission[],
): boolean {
  return permissions.some((p) => can(user, p))
}
